<script setup lang="ts">
import { computed } from 'vue'
import type { EnqueteIndexPagePaginationFragment } from './EnqueteIndexPagePagination.generated'

const props = defineProps<{
  paginationInfo: EnqueteIndexPagePaginationFragment
}>()

const emit = defineEmits<{
  (event: 'move', page: number): void
}>()

type PageIndex = number | 'separator'

const currentPage = computed(
  () => props.paginationInfo.offset / props.paginationInfo.limit + 1
)

const pages = computed<PageIndex[]>(() => {
  const { paginationInfo } = props

  if (paginationInfo.totalCount === 0) {
    return [1]
  }

  const totalPage = Math.ceil(paginationInfo.totalCount / paginationInfo.limit)

  return buildPagination(totalPage, currentPage.value)
})

function buildPagination(totalPages: number, currentPage: number): PageIndex[] {
  const pagination: Set<number> = new Set()

  //
  // まず重複を許可する形で、描画するページ番号を追加する
  //

  pagination.add(1)
  pagination.add(currentPage)
  pagination.add(totalPages)

  // ページ番号のボタンを最大いくつ表示するか
  const PAGE_INDEX_COUNT_MAX = 5
  const pageIndexCount = Math.min(PAGE_INDEX_COUNT_MAX, totalPages)

  // eslint-disable-next-line fp/no-let
  for (let i = 1; pagination.size < pageIndexCount; i++) {
    const prev = currentPage - i
    if (prev > 0) {
      pagination.add(prev)
    }

    const next = currentPage + i
    if (next < totalPages) {
      pagination.add(next)
    }
  }

  //
  // ページ番号が連続しない箇所に 'separator' を追加する
  //

  const sorted = Array.from(pagination).toSorted()

  return sorted.reduce((acc, num) => {
    const prev = acc[acc.length - 1]
    if (prev && typeof prev === 'number') {
      if (num - prev > 1) {
        acc = [...acc, 'separator']
      }
    }

    return [...acc, num]
  }, [] as PageIndex[])
}

const onClickPageButton = (page: number) => {
  emit('move', page)
}
</script>

<template>
  <div>
    <ul :class="$style.ul">
      <li v-for="(page, i) in pages" :key="i" :class="$style.pageItem">
        <button
          v-if="typeof page === 'number'"
          @click="onClickPageButton(page)"
          :class="{
            [$style.page]: true,
            [$style.currentPage]: page === currentPage,
          }"
        >
          {{ page }}
        </button>
        <span v-else-if="page === 'separator'">...</span>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" module>
.ul {
  display: flex;
  gap: 16px;
}

.pageItem {
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page {
  cursor: pointer;
  padding: 8px;

  &:hover {
    text-decoration: underline;
  }
}

.currentPage {
  font-weight: bold;
  text-decoration: underline;
}
</style>
